/**
 * The machine ceiling, where it bites in the console: a refusal from an agent or the hub names
 * the `orgus agent ceiling allow <entry>` command to run on that computer. These turn such a
 * message into the one exact command, so the console can show it with a copy button.
 */

/** What a single-machine hub (`orgus up`) needs once to verify landings and run declared actions. */
export const LANDING_ALLOW_COMMAND = 'orgus agent ceiling allow ext:landing job.run';

/** A ceiling entry: '*', or a capability with a dot or colon in it (job.run, ext:landing, session.start:agent:codex). */
const ENTRY = String.raw`(?:\*|[a-z][\w-]*(?:[.:][\w*-]+)+)`;
const ALLOW = new RegExp(String.raw`orgus agent ceiling allow (${ENTRY}(?:\s+${ENTRY})*)`, 'gi');

/**
 * Every entry the text asks to allow, merged into one command ("allow ext:landing job.run"),
 * or null when the text is not a ceiling refusal. Trailing punctuation is not part of an entry.
 */
export function ceilingCommand(text: string | null | undefined): string | null {
  if (!text) return null;
  const entries: string[] = [];
  for (const m of text.matchAll(ALLOW)) {
    for (const raw of (m[1] ?? '').split(/\s+/)) {
      const entry = raw.replace(/[.,;:)]+$/, '');
      if (entry && !entries.includes(entry)) entries.push(entry);
    }
  }
  return entries.length ? `orgus agent ceiling allow ${entries.join(' ')}` : null;
}

/** What a ceiling entry refuses, in a few words (the Trust table, the machine drawer). */
const CAPABILITY_LABEL: Record<string, string> = {
  '*': 'All writes',
  'session.start': 'Sessions',
  'session.start:terminal': 'Terminal sessions',
  'session.start:headless': 'Headless sessions',
  'session.start:mode:bypassPermissions': 'Sessions that bypass permissions',
  'session.start:agent:assistant': "The assistant's own tools",
  'session.stop': 'Stopping sessions',
  'job.run': 'Commands',
  'job.cancel': 'Cancelling commands',
  'worktree.remove': 'Removing worktrees',
  'worktree.integrate': 'Merging and landing mission work',
  'pipeline.pause': 'Pausing pipelines',
  'pipeline.resume': 'Resuming pipelines',
  'pipeline.restart': 'Restarting pipelines',
  'project.add': 'Adding projects',
  'project.remove': 'Removing projects',
  'term.attach': 'Joining terminals',
  'term.shell': 'Shells',
  'ext:landing': 'Landing queue git work',
  'cwd:contains-home': 'Sessions in a folder holding the Orgus home',
};

export function capabilityLabel(c: string): string {
  return CAPABILITY_LABEL[c] ?? (c.startsWith('ext:') ? `${c.slice(4)} module` : c);
}

/** The command that stops refusing one entry, quoted for a shell when it holds a star. */
export function allowCommand(entry: string): string {
  return `orgus agent ceiling allow ${entry.includes('*') ? `'${entry}'` : entry}`;
}
